
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';
import { toast } from '@/lib/toast';

interface Company {
  id: string;
  name: string;
  agency_name?: string;
  logo_url?: string;
  primary_color: string;
  mindmap_theme_id?: string;
  created_at: string;
  updated_at: string;
}

export function useCompany() {
  const { user } = useAuth();
  const [company, setCompany] = useState<Company | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchCompany = async () => {
    try {
      setIsLoading(true);
      
      if (!user) {
        setCompany(null);
        return;
      }

      console.log('Fetching company for user:', user.id);

      // Get user's company data
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (userError) {
        console.error('Error fetching user company_id:', userError);
        return;
      }

      if (!userData?.company_id) {
        console.log('User has no company_id');
        return;
      }

      console.log('User company_id:', userData.company_id);

      // Check if user is a collaborator and should use another company's data
      const { data: collaboratorData } = await supabase
        .from('collaborators')
        .select('company_id')
        .eq('collaborator_id', user.id)
        .eq('status', 'active')
        .maybeSingle();

      const targetCompanyId = collaboratorData?.company_id || userData.company_id;
      
      console.log('Target company_id:', targetCompanyId);

      // Fetch company data
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('*')
        .eq('id', targetCompanyId)
        .single();

      if (companyError) {
        console.error('Error fetching company:', companyError);
        return;
      }

      console.log('Company data loaded:', companyData);
      setCompany(companyData);
    } catch (error) {
      console.error('Error loading company:', error);
      setCompany(null);
    } finally {
      setIsLoading(false);
    }
  };

  const updateCompany = async (updates: Partial<Omit<Company, 'id' | 'created_at' | 'updated_at'>>) => {
    try {
      if (!company) {
        toast.error('Nenhuma empresa encontrada');
        return false;
      }

      console.log('Updating company with:', updates);

      const { error } = await supabase
        .from('companies')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', company.id);

      if (error) {
        console.error('Error updating company:', error);
        throw error;
      }

      await fetchCompany(); // Refresh company data
      toast.success('Dados da empresa atualizados com sucesso');
      return true;
    } catch (error: any) {
      console.error('Error updating company:', error);
      toast.error(error.message || 'Erro ao atualizar dados da empresa');
      return false;
    }
  };

  useEffect(() => {
    fetchCompany();
  }, [user]);

  return {
    company,
    isLoading,
    fetchCompany,
    updateCompany,
    // Branding properties for easier access
    logoUrl: company?.logo_url || null,
    primaryColor: company?.primary_color || '#0448ff',
    agencyName: company?.agency_name || company?.name || '',
    mindmapThemeId: company?.mindmap_theme_id || 'light-smooth'
  };
}
